import { ref, watch } from 'vue';
import { API_BASE } from '../constants.js';
import { useEditorMode } from './useEditorMode.js';
import { strapiSources } from './useLayers.js';
import { disclaimerOnce, setJwtCookie, userInit } from './useUserManage.js';

const loginWarning = ref(false);
/**
 * What became of the edit the login link carried, told to the user once it has
 * been applied. Empty as long as there is nothing to report.
 */
const contextWarning = ref('');
const contextSuccess = ref('');

const SAVE_SUCCESS =
  'Ihre Anlage ist bestätigt und wird nach Freigabe durch die Gemeinde allgemein sichtbar.';
const SAVE_FAILURE =
  'Ihre Anlage konnte nicht gespeichert werden! Bitte erfassen Sie die Anlage erneut.';

/**
 * Reports how the anlage a login link carried fared. Only one message is shown,
 * and a failure outranks a success: both snackbars occupy the same spot, and
 * the failure is the one that asks something of the user.
 * @param {boolean} ok
 */
function reportContext(ok) {
  if (!ok) {
    contextWarning.value = SAVE_FAILURE;
    contextSuccess.value = '';
  } else if (!contextWarning.value) {
    contextSuccess.value = SAVE_SUCCESS;
  }
}

/**
 * Reloads a Strapi source, so items saved while logging in and items only
 * visible to the logged in user appear on the map. The map may still be
 * initializing, and a load started before the login would not contain them.
 * @param {string} source
 */
function refreshStrapiSource(source) {
  const strapiSource = strapiSources.value[source];
  if (!strapiSource) {
    const unwatch = watch(strapiSources, (sources) => {
      if (sources[source]) {
        unwatch();
        refreshStrapiSource(source);
      }
    });
    return;
  }
  if (strapiSource.loading) {
    strapiSource.once('featuresloadend', () => strapiSource.refresh());
    return;
  }
  strapiSource.refresh();
}

/**
 * @param {string} token
 * @returns {Promise<boolean>}
 */
export async function loginPasswordless(token) {
  const safeToken = encodeURIComponent(token);
  try {
    const response = await fetch(
      `${API_BASE}/passwordless/login?loginToken=${safeToken}`
    );
    if (!response.ok) {
      return false;
    }
    const data = await response.json();
    if (data.jwt) {
      setJwtCookie(data.jwt);
      await userInit();
      const sourcesToRefresh = new Set(Object.keys(strapiSources.value));
      if (data.context) {
        for (const key in data.context) {
          sourcesToRefresh.add(key);
          const { saveItem, editableObject, editorMode } = useEditorMode(key);
          const { mode, saveItem: pendingItem } = data.context[key];
          if (pendingItem) {
            editableObject.value = pendingItem;
            const saved = await saveItem(); // eslint-disable-line no-await-in-loop
            reportContext(saved);
          }
          if (mode) {
            // The link was requested to carry on with an action that needs a
            // login, so the editor resumes it now that the user has one.
            editorMode.value = { mode, snack: true };
          }
        }
      }
      sourcesToRefresh.forEach((source) => refreshStrapiSource(source));
      return true;
    }
  } catch (error) {
    console.error(error); // eslint-disable-line no-console
  }
  return false;
}

const url = new URL(window.location.href);
const loginToken = url.searchParams.get('loginToken');

if (loginToken) {
  // The disclaimer would cover the map while the pending edit is applied, and
  // users following a login link have already seen and accepted it.
  disclaimerOnce.value = false;
  url.searchParams.delete('loginToken');
  window.history.replaceState({}, '', url);
  loginWarning.value = false;
  (async () => {
    const success = await loginPasswordless(loginToken.toString());
    if (!success) {
      loginWarning.value = true;
    }
  })();
}

export const usePasswordless = () => ({
  loginPasswordless,
  loginWarning,
  contextWarning,
  contextSuccess,
});
